import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  Switch,
  SimpleGrid,
  Spinner,
  Field,
  NativeSelect,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiArrowLeft } from 'react-icons/fi'
import {
  useAdminCommitteeDetail,
  useCreateCommittee,
  useUpdateCommittee,
} from '../hooks/useAdminCommittees'
import { useCommitteeOptions } from '../hooks/useAdminUsers'
import { useAuth } from '../hooks/useAuth'
import type { SaveCommitteeRequest } from '../types/admin'
import { StickyActionFooter } from '../components/shared'

export function CommitteeFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const { data: existing, isLoading: isLoadingDetail } = useAdminCommitteeDetail(id)
  const createCommittee = useCreateCommittee()
  const updateCommittee = useUpdateCommittee(id || '')
  // WL-3: only a SuperAdmin may set/change a committee's parent (a provisioning/billing act). A plain
  // Admin edits contact info on the same form, so the parent control is hidden rather than the page.
  const { isSuperAdmin } = useAuth()

  const [label, setLabel] = useState('')
  const [labelSpanish, setLabelSpanish] = useState('')
  const [description, setDescription] = useState('')
  const [descriptionSpanish, setDescriptionSpanish] = useState('')
  const [streetAddress, setStreetAddress] = useState('')
  const [city, setCity] = useState('')
  const [zip, setZip] = useState('')
  const [county, setCounty] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [poc, setPoc] = useState('')
  const [pocPhone, setPocPhone] = useState('')
  const [pocEmail, setPocEmail] = useState('')
  const [defaultReplyToEmail, setDefaultReplyToEmail] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [parentCommitteeId, setParentCommitteeId] = useState<string | null>(null)
  const { data: committeeOptions } = useCommitteeOptions()

  useEffect(() => {
    if (existing) {
      setLabel(existing.label)
      setLabelSpanish(existing.labelSpanish || '')
      setDescription(existing.description || '')
      setDescriptionSpanish(existing.descriptionSpanish || '')
      setStreetAddress(existing.streetAddress || '')
      setCity(existing.city || '')
      setZip(existing.zip || '')
      setCounty(existing.county || '')
      setEmail(existing.email || '')
      setPhone(existing.phone || '')
      setPoc(existing.poc || '')
      setPocPhone(existing.pocPhone || '')
      setPocEmail(existing.pocEmail || '')
      setDefaultReplyToEmail(existing.defaultReplyToEmail || '')
      setIsActive(existing.isActive)
      setParentCommitteeId(existing.parentCommitteeId ?? null)
    }
  }, [existing])

  const buildRequest = (): SaveCommitteeRequest => ({
    label,
    labelSpanish: labelSpanish || undefined,
    description: description || undefined,
    descriptionSpanish: descriptionSpanish || undefined,
    streetAddress: streetAddress || undefined,
    city: city || undefined,
    zip: zip || undefined,
    county: county || undefined,
    email: email || undefined,
    phone: phone || undefined,
    poc: poc || undefined,
    pocPhone: pocPhone || undefined,
    pocEmail: pocEmail || undefined,
    defaultReplyToEmail: defaultReplyToEmail || undefined,
    isActive,
    parentCommitteeId: parentCommitteeId || undefined,
  })

  const handleSubmit = async () => {
    if (!label.trim()) {
      toaster.create({ title: 'Name is required', type: 'warning', duration: 3000 })
      return
    }

    try {
      const request = buildRequest()
      if (isEditMode) {
        await updateCommittee.mutateAsync(request)
        toaster.create({ title: 'Committee updated', type: 'success', duration: 3000 })
      } else {
        await createCommittee.mutateAsync(request)
        toaster.create({ title: 'Committee created', type: 'success', duration: 3000 })
      }
      navigate('/dashboard/admin/committees')
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'An error occurred'
      toaster.create({ title: 'Error', description: message, type: 'error', duration: 5000 })
    }
  }

  const isSaving = createCommittee.isPending || updateCommittee.isPending

  if (isEditMode && isLoadingDetail) {
    return (
      <Container maxW="container.lg" py={8}>
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="gray.500">Loading committee...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" py={8} px={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch" pb={{ base: '96px', md: 0 }}>
        {/* Header */}
        <HStack justify="space-between">
          <HStack gap={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard/admin/committees')}><FiArrowLeft />Back
                          </Button>
            <Box>
              <Heading size="lg">
                {isEditMode ? 'Edit Committee' : 'Create Committee'}
              </Heading>
              {isEditMode && existing && (
                <Text color="gray.500" mt={1} fontSize="sm">
                  Created by {existing.createdByName} &middot; {existing.memberCount} member(s)
                </Text>
              )}
            </Box>
          </HStack>
          <HStack gap={3} display={{ base: 'none', md: 'flex' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard/admin/committees')}
            >
              Cancel
            </Button>
            <Button
              colorPalette="brand"
              size="sm"
              onClick={handleSubmit}
              loading={isSaving}
            >
              {isEditMode ? 'Save Changes' : 'Create Committee'}
            </Button>
          </HStack>
        </HStack>

        {/* General Info */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="sm" mb={4}>General Information</Heading>
          <VStack gap={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
              <Field.Root required>
                <Field.Label fontSize="sm" color="gray.600">Name</Field.Label>
                <Input
                  size="sm"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Committee name"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Name (Spanish)</Field.Label>
                <Input
                  size="sm"
                  value={labelSpanish}
                  onChange={(e) => setLabelSpanish(e.target.value)}
                />
              </Field.Root>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Description</Field.Label>
                <Textarea
                  size="sm"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Description (Spanish)</Field.Label>
                <Textarea
                  size="sm"
                  rows={3}
                  value={descriptionSpanish}
                  onChange={(e) => setDescriptionSpanish(e.target.value)}
                />
              </Field.Root>
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Parent Committee — WL-3: SuperAdmin-only (provisioning/billing act) */}
        {isSuperAdmin && (
          <Box bg="surface" p={6} borderRadius="lg" shadow="md">
            <Heading size="sm" mb={4}>Hierarchy</Heading>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Parent Committee</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  placeholder="None (top-level committee)"
                  value={parentCommitteeId ?? ''}
                  onChange={(e) => setParentCommitteeId(e.target.value || null)}
                >
                  {committeeOptions
                    ?.filter(c => c.id !== id)
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Assign a parent to make this a Local committee under a National.
              </Text>
            </Field.Root>
          </Box>
        )}

        {/* Location */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="sm" mb={4}>Location</Heading>
          <VStack gap={4}>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Street Address</Field.Label>
              <Input
                size="sm"
                value={streetAddress}
                onChange={(e) => setStreetAddress(e.target.value)}
              />
            </Field.Root>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} w="full">
              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">City</Field.Label>
                <Input
                  size="sm"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Zip</Field.Label>
                <Input
                  size="sm"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">County</Field.Label>
                <Input
                  size="sm"
                  value={county}
                  onChange={(e) => setCounty(e.target.value)}
                />
              </Field.Root>
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Contact Info */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="sm" mb={4}>Contact Information</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Email</Field.Label>
              <Input
                size="sm"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Phone</Field.Label>
              <Input
                size="sm"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm" color="fg.muted">Default reply-to email</Field.Label>
              <Input
                size="sm"
                type="email"
                value={defaultReplyToEmail}
                onChange={(e) => setDefaultReplyToEmail(e.target.value)}
                placeholder="lobbyday@yourunion.org"
              />
              <Field.HelperText fontSize="xs" color="fg.muted">
                Pre-fills the Reply-to on emails organizers send from this committee. Leave blank to
                default to each sender's own email. Recipients still see your sending address in the From line.
              </Field.HelperText>
            </Field.Root>
          </SimpleGrid>
        </Box>

        {/* Point of Contact */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <Heading size="sm" mb={4}>Point of Contact</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Name</Field.Label>
              <Input
                size="sm"
                value={poc}
                onChange={(e) => setPoc(e.target.value)}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Phone</Field.Label>
              <Input
                size="sm"
                value={pocPhone}
                onChange={(e) => setPocPhone(e.target.value)}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="sm" color="gray.600">Email</Field.Label>
              <Input
                size="sm"
                type="email"
                value={pocEmail}
                onChange={(e) => setPocEmail(e.target.value)}
              />
            </Field.Root>
          </SimpleGrid>
        </Box>

        {/* Status */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md">
          <HStack justify="space-between">
            <Box>
              <Heading size="sm">Status</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Inactive committees will not appear in user assignment dropdowns
              </Text>
            </Box>
            <Field.Root display="flex" alignItems="center" w="auto">
              <Field.Label fontSize="sm" color="gray.600" mb={0} mr={3}>Active</Field.Label>
              <Switch.Root
                checked={isActive}
                onCheckedChange={(e) => setIsActive(e.checked === true)}
                colorPalette="brand"
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            </Field.Root>
          </HStack>
        </Box>
      </VStack>

      <StickyActionFooter>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/dashboard/admin/committees')}
        >
          Cancel
        </Button>
        <Button
          colorPalette="brand"
          size="sm"
          onClick={handleSubmit}
          loading={isSaving}
        >
          {isEditMode ? 'Save Changes' : 'Create Committee'}
        </Button>
      </StickyActionFooter>
    </Container>
  );
}
